<template>
  <Modal v-bind="$attrs" :key="id" name="newCard">
    <template #header>
        <h1 v-if="!editMode">
          Add new Dashboard Card
        </h1>
        <h1 v-else>
          Edit dashboard card
        </h1>
    </template>
    <template #body>
        <div class="grid grid-body gap-4">
            <div class="left">
                <div class="modal-navigation flex justify-evenly flex-nowrap mb-2">
                    <div 
                        v-for="(tab, index) in tabs.title"
                        :key="index"
                        class="nav-item flex-grow text-center py-2 border-b-2 cursor-pointer" 
                        :class="{ 'border-sparkYellow text-bold': index === tabs.active, 'opacity-40 cursor-not-allowed': index === 1 && disabledOptions }"
                        @click="changeTab(index)"
                    >{{ tabs.title[index] }}</div>
                </div>
                <div class="modal-tabs">
                    <div v-show="tabs.active === 0" class="tab">
                        <div class="my-row">
                            <div class="label-wrapper reportId">
                                <span class="font-bold text-4xl">1.</span>
                                <label>Select Report <vue-feather v-tippy="{ content: 'You can choose between reports with chart assign to it', appendTo: 'parent', theme: 'trg' }" type="help-circle" stroke-width="1" stroke="#6b7280" size="14px" /></label>
                            </div>
                            <div class="form">
                                <multiselect v-model="reportSelected" :loading="loadingReports" :multiple="false" group-label="name" group-values="report" :options="reportStore.reportsList" label="name" track-by="id" :close-on-select="true" :show-labels="false" placeholder="Select" @select="onSelect" @remove="onDeselect()" />
                            </div>
                        </div>
                        <div class="my-row transition" :class="!reportSelected ? 'disabled' : null">
                            <div class="label-wrapper name">
                                <span class="font-bold text-4xl">2.</span>
                                <label>Card title</label>
                            </div>
                            <div class="form">
                                <input ref="name" v-model="title" type="text" name="name" maxlength="100" required :disabled="!reportSelected" @keyup.enter="submit">
                            </div>
                        </div>
                    </div>
                    <div v-show="tabs.active === 1" class="tab">
                        <div class="my-row">
                            <div class="label-wrapper">
                                <!-- <span class="font-bold text-4xl">1.</span> -->
                                <label>Display as</label>
                            </div>
                            <div class="form flex flex-wrap space-x-2">
                                <div 
                                    v-for="type in chart.options.displayAs"
                                    :key="type.id"
                                    v-tippy="{ content: type.tooltip, appendTo: 'parent', theme: 'trg' }"
                                    class="icon rounded border w-10 h-10 flex justify-center items-center"
                                    :class="{'bg-pureNavy': isActive(type.id) }"
                                    @click="setChartType(type.id)"
                                >
                                    <vue-feather :type="type.icon" stroke-width="1" stroke="#6b7280" :class="{'transform rotate-90' : type.rotateIcon}" />
                                </div>
                                
                            </div>
                        </div>
                        <div class="my-row">
                            <div class="label-wrapper">
                                <!-- <span class="font-bold text-4xl">2.</span> -->
                                <label>Legend</label>
                            </div>
                            <div class="form flex flex-col space-y-3">
                                <div class="option flex justify-between items-center space-x-2">
                                    <div>Show</div>
                                        <Toggle v-model="chart.options.showLegend" class="toggle-pure-navy" />
                                </div>
                                <div class="option flex justify-between items-center space-x-2">
                                    <div :class="{'opacity-50': !chart.options.showLegend}">
                                      Position
                                    </div>
                                    <multiselect v-model="chart.options.legendPosition" :disabled="!chart.options.showLegend" :multiple="false" :options="chart.options.legendOptions" :close-on-select="true" :show-labels="false" placeholder="Select" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="bg-gray-100 border h-full p-2 right rounded text-center">
                <div class="gap-2 grid h-full">
                    <h2 class="font-extrabold mb-4 text-2xl">
                      Preview
                    </h2>
                    <div v-if="chart.loading" class="flex h-full items-center justify-center">
                        <vue-feather type="loader" :stroke="theme.colors.steelBlue" animation="spin" />
                    </div>
                    <p v-else-if="!!!chart.data" class="self-center text-gray-500">
                      Select report first, than you will see chart which belongs to it.
                    </p>
                    <chart-container 
                        v-else
                        key="modal"
                        :data="chart.data"
                        :responsive="true"
                        :show-legend="chart.options.showLegend"
                        :legend-position="chart.options.legendPosition"
                        :index-axis="indexAxis"
                        :chart-type="chart.data.chart_type_id"
                        guid="modal"
                    />
                </div>
            </div>
        </div>

    </template>
    <template #footer>
        <div class="btn btn-primary submit" :class="!isValid ? 'disabled' : ''" @click="submit">
            <vue-feather size="24px" type="send" /> Submit
        </div>
    </template>
</Modal>
</template>

<script>
import { mapGetters } from 'vuex'
import Modal from '../modules/modals/components/Modal'
import ChartContainer from "./ChartContainer"
import Toggle from '@vueform/toggle'
import resolveConfig from 'tailwindcss/resolveConfig'
import tailwindConfig from '../../tailwind.config.js'
import {useDashboardStore} from "@/store/dashboard";
import {useReportStore} from "@/store/report";

export default {
    setup() {
      const dashboardStore = useDashboardStore()
      const reportStore = useReportStore()

      return { dashboardStore, reportStore }
    },
    name: 'ModalNewCard',
    components: {
        Modal,
        ChartContainer,
        Toggle,
    },
    data(){
        const {theme} = resolveConfig(tailwindConfig)
        return {
            id: null,
            title: '',
            reportSelected: null,
            loadingReports: true,
            theme: theme,
            chart: {
                info: {
                    id: null,
                    name: ''
                },
                loading: false,
                data: null,
                options: {
                    displayAs: [
                        {
                            id: 2,
                            icon: 'bar-chart-2',
                            tooltip: 'Bar chart - vertically',
                            rotateIcon: false,
                        },
                        {
                            id: 4,
                            icon: 'bar-chart-2',
                            tooltip: 'Bar chart - horizontally',
                            rotateIcon: true,
                        },
                        {
                            id: 1,
                            icon: 'pie-chart',
                            tooltip: 'Pie chart',
                            rotateIcon: false,
                        },
                        {
                            id: 3,
                            icon: 'activity',
                            tooltip: 'Line chart',
                            rotateIcon: false,
                        },
                    ],
                    showLegend: true,
                    legendPosition: 'top',
                    legendOptions: ['top','bottom', 'left', 'right'],
                }
            },
            report_id: null,
            tabs: {
                active: 0,
                title: ["General", "Options"]
            },
        }
    },
    computed: {
        ...mapGetters({
            item: 'item',
        }),
        editMode() {
            if(this.id){
                return true
            } else {
                return false
            }
        },
        indexAxis(){
            if( this.chart.data && this.chart.data.chart_type_id === 4) {
                return 'y'
            } else {
                return 'x'
            }
        },
        isValid(){
            if( this.reportSelected !== null && typeof this.reportSelected == 'object' && !!this.chart.data && !this.chart.loading) {
                return true
            }
                return false
        },
        disabledOptions(){
            return this.reportSelected === null
        },
    },
    methods: {
        submit(){
            if(this.isValid){
                let output = {
                    name: this.title,
                    report_id: this.reportSelected.id,
                    remove: false,
                    placement: this.editMode ? this.$attrs.item.placement : null,
                    order: this.editMode ? this.$attrs.item.order : null,
                    id: this.id,
                    chart_options: {
                        type: this.chart.data.chart_type_id,
                        show_legend: this.chart.options.showLegend,
                        legend_position: this.chart.options.legendPosition,
                    },
                }
                this.dashboardStore.setCard({output, edit: this.editMode})
                this.$attrs.close()
            }
        },
        onDeselect(){
            this.title = ''
            this.chart.data = null
        },
        onSelect(item){
            this.title = item.name
            this.chart.info = item.chart
            this.report_id = item.id
            if(this.reportStore.chart(item.id) != undefined){
                this.chart.data = this.reportStore.chart(item.id)
            } else {
                this.chart.loading = true
                this.reportStore.getChart(item).then(response => {
                    this.chart.data = response
                    this.chart.loading = false
                })

            }
            this.$nextTick(()=>{
                this.$refs.name.focus()
            })
        },
        changeTab(index){
            if(!this.disabledOptions) {
                this.tabs.active = index
            }
        },
        isActive(id) {
            if(this.chart.data && this.chart.data.chart_type_id) {
                return id === this.chart.data.chart_type_id
            } else {
                return false
            }
        },
        setChartType(id){
            if(this.chart.data && this.chart.data.chart_type_id){
                this.chart.data.chart_type_id = id
            }
        }
    },
    mounted() {
        if(this.$attrs.id) {
            this.id = this.$attrs.id
        }
        this.loadingReports = true
        this.reportStore.getReports().then(() => {
            const card = this.$attrs.item
            if(this.$attrs.id && card && card.report_id) {
                const report = this.reportStore.findReport(card.report_id)
                this.title = card.name
                this.chart.data = this.reportStore.chart(card.report_id)
                this.report_id = card.report_id
                this.reportSelected = report
                this.chart.info = report.chart
                if(Object.keys(card.chart_options).length) {
                    this.chart.data.chart_type_id = card.chart_options.type
                    this.chart.options.legendPosition = card.chart_options.legend_position
                    this.chart.options.showLegend = card.chart_options.show_legend
                } else {
                    this.chart.data.chart_type_id = this.reportStore.chart(this.report_id).chart_type_id
                    this.chart.options.legendPosition = 'top'
                    this.chart.options.showLegend = true
                }
            }
            this.loadingReports = false
        })
    }
}
</script>

<style src="@vueform/toggle/themes/default.css"></style>
<style lang="sass" scoped>
h1
    font-size: 25px!important
label
    font-weight: 700
    font-size: 17px
input, select, textarea
    border: 1px solid #F0F1F3!important
    border-radius: 7px
    width: 100%
input[type='checkbox']
    min-width: 25px
    width: inherit
    height: 25px
.label-wrapper
    display: flex
    align-items: baseline
    margin-bottom: 4px
    label
        margin-left: 10px
.my-row
    &.disabled
        @apply opacity-40
    .col
        flex: 1
    &:not(:last-child)
        margin-bottom: 1.5rem
    .form
        padding-left: 30px
.grid-body
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr))
.right>.grid
    grid-template-rows: 37px 1fr
</style>
<style lang="sass">
.toggle-switch
    margin: 0!important
    input
        display: none
.toggle-pure-navy
  --toggle-bg-on: #00283A
  --toggle-border-on: #00283A
</style>
